import {
  Badge,
  Button,
  Card,
  mergeClasses,
  Text,
  Title2,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import {
  bundleIcon,
  ChevronDown16Filled,
  ChevronDown16Regular,
  ChevronRight16Filled,
  ChevronRight16Regular,
  Checkmark16Filled,
  Dismiss16Filled,
  Link16Filled,
  Link16Regular,
  Copy16Filled,
  Copy16Regular,
} from '@fluentui/react-icons';
import { useState } from 'react';

import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard/useCopyToClipboard';
import type { SkillExecutionDetail } from '../../../types/debug';

import type { SkillExecutionTableProps } from './SkillExecutionTable.interface';
import { useStyles } from './SkillExecutionTable.style';

const ExpandIcon = bundleIcon(
  ChevronDown16Filled,
  ChevronDown16Regular,
) as React.FC;
const CollapseIcon = bundleIcon(
  ChevronRight16Filled,
  ChevronRight16Regular,
) as React.FC;
const LinkIcon = bundleIcon(Link16Filled, Link16Regular) as React.FC;
const CopyIcon = bundleIcon(Copy16Filled, Copy16Regular) as React.FC;

function statusBadgeColor(
  status: string,
): 'success' | 'danger' | 'warning' | 'informative' {
  const s = status.toLowerCase();
  if (s === 'success') return 'success';
  if (s === 'failure' || s === 'error') return 'danger';
  if (s === 'partialresult') return 'warning';
  return 'informative';
}

function SkillRow({
  skill,
  index,
  isExpanded,
  onToggle,
}: {
  skill: SkillExecutionDetail;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const styles = useStyles();
  const [showFullResponse, setShowFullResponse] = useState(false);
  const [showSuggestedAction, setShowSuggestedAction] = useState(false);
  const { copied: copiedResponse, copy: copyResponse } = useCopyToClipboard();

  const isPass =
    skill.executionStatus.SkillResponseStatus === 'Success' ||
    skill.executionStatus.LLMCompletionStatus === 'Success';

  const responsePreview =
    skill.response && skill.response.length > 500 && !showFullResponse
      ? skill.response.slice(0, 500) + '...'
      : skill.response;

  return (
    <>
      <tr
        className={mergeClasses(styles.tr, isExpanded && styles.trExpanded)}
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <td className={styles.td}>
          <div className={styles.skillRowContent}>
            {isExpanded ? <ExpandIcon /> : <CollapseIcon />}
            {isPass ? (
              <Checkmark16Filled
                style={{ color: tokens.colorPaletteGreenForeground1 }}
              />
            ) : (
              <Dismiss16Filled
                style={{ color: tokens.colorPaletteRedForeground1 }}
              />
            )}
            <span className={styles.skillName}>{skill.executedSkillId}</span>
            <Badge
              appearance="tint"
              color={statusBadgeColor(
                skill.executionStatus.LLMCompletionStatus,
              )}
              size="small"
            >
              LLM: {skill.executionStatus.LLMCompletionStatus}
            </Badge>
            <Badge
              appearance="tint"
              color={statusBadgeColor(
                skill.executionStatus.SkillResponseStatus,
              )}
              size="small"
            >
              Skill: {skill.executionStatus.SkillResponseStatus}
            </Badge>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className={styles.expandedRow}>
          <td className={styles.expandedCell} colSpan={1}>
            {/* Intent / agent type */}
            {(skill.detectedIntent ??
              (skill.agentType && skill.agentType !== 'Unknown')) && (
              <div className={styles.detailSection}>
                <div className={styles.maBadges}>
                  {skill.detectedIntent && (
                    <Badge
                      appearance="outline"
                      color="informative"
                      size="small"
                    >
                      {skill.detectedIntent}
                    </Badge>
                  )}
                  {skill.agentType && skill.agentType !== 'Unknown' && (
                    <Badge appearance="outline" color="brand" size="small">
                      {skill.agentType}
                    </Badge>
                  )}
                </div>
              </div>
            )}
            {/* Parameters */}
            {skill.parameters && skill.parameters.length > 0 && (
              <div className={styles.detailSection}>
                <Text className={styles.detailLabel}>
                  Parameters ({skill.parameters.length.toString()})
                </Text>
                <div
                  className={styles.detailContent}
                  data-testid="skill-parameters"
                >
                  {skill.parameters
                    .map((parameter) => parameter ?? 'null')
                    .join('\n')}
                </div>
              </div>
            )}

            {/* Response */}
            {skill.response && (
              <div className={styles.detailSection}>
                <Text className={styles.detailLabel}>
                  Response ({skill.response.length.toLocaleString()} chars)
                </Text>
                <div className={styles.detailContent}>{responsePreview}</div>
                {skill.response.length > 500 && (
                  <div className={styles.responseActions}>
                    <Button
                      appearance="subtle"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFullResponse((f) => !f);
                      }}
                    >
                      {showFullResponse ? 'Show less' : 'Show full response'}
                    </Button>
                    <Tooltip
                      content={
                        copiedResponse ? 'Copied!' : 'Copy full response text'
                      }
                      relationship="label"
                    >
                      <Button
                        className={styles.copyResponseBtn}
                        appearance="subtle"
                        size="small"
                        icon={<CopyIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyResponse(skill.response ?? '');
                        }}
                        aria-label="Copy full response to clipboard"
                      >
                        {copiedResponse ? 'Copied!' : 'Copy Full Response'}
                      </Button>
                    </Tooltip>
                  </div>
                )}
              </div>
            )}

            {/* Citations (PA) */}
            {skill.citations && skill.citations.length > 0 && (
              <div className={styles.detailSection}>
                <Text className={styles.detailLabel}>
                  Citations ({skill.citations.length.toString()})
                </Text>
                <div className={styles.citationsList}>
                  {skill.citations.map((c) => (
                    <div
                      key={`${index.toString()}-cit-${c.citationId.toString()}`}
                      className={styles.citationItem}
                    >
                      <LinkIcon />
                      <a
                        href={c.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.citationLink}
                        onClick={(e) => e.stopPropagation()}
                      >
                        [{c.citationId}] {c.source ?? 'Source'}
                      </a>
                      {c.sensitivityTag && (
                        <Tooltip
                          content={
                            <span className={styles.sensitivityTooltip}>
                              {c.sensitivityTag.description ??
                                c.sensitivityTag.title}
                              {c.sensitivityTag.priority != null && (
                                <>
                                  {' '}
                                  (Priority:{' '}
                                  {c.sensitivityTag.priority.toString()})
                                </>
                              )}
                            </span>
                          }
                          relationship="description"
                        >
                          <Badge
                            appearance="tint"
                            color="warning"
                            size="small"
                            style={{ cursor: 'help' }}
                          >
                            {c.sensitivityTag.title}
                          </Badge>
                        </Tooltip>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Suggested Action (PA) */}
            {skill.suggestedAction != null && (
              <div className={styles.detailSection}>
                <button
                  type="button"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: tokens.spacingHorizontalXS,
                    cursor: 'pointer',
                    border: 'none',
                    backgroundColor: 'transparent',
                    padding: 0,
                    fontFamily: 'inherit',
                  }}
                  aria-expanded={showSuggestedAction}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSuggestedAction((s) => !s);
                  }}
                >
                  {showSuggestedAction ? <ExpandIcon /> : <CollapseIcon />}
                  <Text className={styles.detailLabel}>
                    Suggested Action (ODSL) (
                    {JSON.stringify(
                      skill.suggestedAction,
                      null,
                      2,
                    ).length.toLocaleString()}{' '}
                    chars)
                  </Text>
                </button>
                {showSuggestedAction && (
                  <div className={styles.detailContent}>
                    {JSON.stringify(skill.suggestedAction, null, 2)}
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function SkillExecutionTable({ data }: SkillExecutionTableProps) {
  const styles = useStyles();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedRows(new Set(data.skillExecutionDetails.map((_, i) => i)));
  };

  const collapseAll = () => {
    setExpandedRows(new Set());
  };

  if (data.skillExecutionDetails.length === 0) {
    return (
      <Card className={styles.root}>
        <Title2 className={styles.headerTitle}>Skill Execution Pipeline</Title2>
        <div className={styles.emptyState}>
          No skill execution details recorded for this interaction.
        </div>
      </Card>
    );
  }

  return (
    <Card className={styles.root}>
      <div className={styles.headerRow}>
        <Title2 className={styles.headerTitle}>Skill Execution Pipeline</Title2>
        <div className={styles.headerActions}>
          <Button appearance="subtle" size="small" onClick={expandAll}>
            Expand All
          </Button>
          <Button appearance="subtle" size="small" onClick={collapseAll}>
            Collapse
          </Button>
        </div>
      </div>

      {/* Enabled Skills — shown first */}
      {data.enabledSkills.length > 0 && (
        <>
          <Text>Enabled Skills ({data.enabledSkills.length.toString()})</Text>
          <div className={styles.enabledSkills}>
            {data.enabledSkills.map((skill) => (
              <Badge
                key={skill}
                appearance="outline"
                color="informative"
                size="small"
              >
                {skill}
              </Badge>
            ))}
          </div>
        </>
      )}

      <Text>
        Executed Skills ({data.skillExecutionDetails.length.toString()})
      </Text>
      <table className={styles.table}>
        <tbody>
          {data.skillExecutionDetails.map((skill, idx) => (
            <SkillRow
              key={`${idx.toString()}-${skill.executedSkillId}`}
              skill={skill}
              index={idx}
              isExpanded={expandedRows.has(idx)}
              onToggle={() => toggleRow(idx)}
            />
          ))}
        </tbody>
      </table>
    </Card>
  );
}
